import * as React from "react"
import type { ChainedCommands } from "@tiptap/react"
import { type Editor } from "@tiptap/react"
import { NodeSelection } from "prosemirror-state"

// --- Hooks ---
import { useTiptapEditor } from "../../../hooks/use-tiptap-editor"

// --- Lib ---
import {
  isExtensionAvailable,
  isNodeTypeSelected,
} from "../../../lib/tiptap-utils"

// --- Icons ---
import { AlignCenterIcon } from "../../../components/tiptap-icons/align-center-icon"
import { AlignJustifyIcon } from "../../../components/tiptap-icons/align-justify-icon"
import { AlignLeftIcon } from "../../../components/tiptap-icons/align-left-icon"
import { AlignRightIcon } from "../../../components/tiptap-icons/align-right-icon"

export type TextAlign = "left" | "center" | "right" | "justify"

// ADICIONE aqui todos os nomes de nó de imagem usados no seu editor:
const IMAGE_NODES = ["image", "resizableImage"] as const
type ImageNodeName = typeof IMAGE_NODES[number]

export interface UseTextAlignConfig {
  editor?: Editor | null
  align: TextAlign
  hideWhenUnavailable?: boolean
  onAligned?: () => void
}

export const TEXT_ALIGN_SHORTCUT_KEYS: Record<TextAlign, string> = {
  left: "mod+shift+l",
  center: "mod+shift+e",
  right: "mod+shift+r",
  justify: "mod+shift+j",
}

export const textAlignIcons = {
  left: AlignLeftIcon,
  center: AlignCenterIcon,
  right: AlignRightIcon,
  justify: AlignJustifyIcon,
}

export const textAlignLabels: Record<TextAlign, string> = {
  left: "Align left",
  center: "Align center",
  right: "Align right",
  justify: "Align justify",
}

/** Retorna o nome do nó de imagem ativo, se houver. */
function getActiveImageNodeName(editor: Editor): ImageNodeName | null {
  for (const name of IMAGE_NODES) {
    if (editor.isActive(name)) return name
  }
  return null
}

/** Seleciona a imagem imediatamente antes/depois do cursor e retorna o nome do nó. */
function selectAdjacentImageAndGetName(editor: Editor): ImageNodeName | null {
  const { state, view } = editor
  const { $from } = state.selection as any
  const before = $from.nodeBefore
  const after = $from.nodeAfter

  if (before && IMAGE_NODES.includes(before.type?.name as ImageNodeName)) {
    const pos = $from.pos - before.nodeSize
    view.dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos)))
    return before.type.name as ImageNodeName
  }

  if (after && IMAGE_NODES.includes(after.type?.name as ImageNodeName)) {
    const pos = $from.pos
    view.dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos)))
    return after.type.name as ImageNodeName
  }

  return null
}

/**
 * Habilita o botão: true se dá pra alinhar texto OU alguma imagem.
 */
export function canSetTextAlign(
  editor: Editor | null,
  align: TextAlign
): boolean {
  if (!editor || !editor.isEditable) return false

  const hasTextAlign = isExtensionAvailable(editor, "textAlign")
  const hasAnyImage = IMAGE_NODES.some((n) => isExtensionAvailable(editor, n))
  if (!hasTextAlign && !hasAnyImage) return false

  // imagem ativa ou node selection de imagem → habilita
  if (
    getActiveImageNodeName(editor) ||
    isNodeTypeSelected(editor, IMAGE_NODES as unknown as string[])
  ) {
    return true
  }

  // caso contrário, verifica alinhamento de bloco/texto
  return editor.can().setTextAlign(align)
}

export function hasSetTextAlign(
  commands: ChainedCommands
): commands is ChainedCommands & {
  setTextAlign: (align: TextAlign) => ChainedCommands
} {
  return "setTextAlign" in commands
}

/**
 * Ativo? Considera atributo `textAlign` em imagens e em blocos/texto.
 */
export function isTextAlignActive(
  editor: Editor | null,
  align: TextAlign
): boolean {
  if (!editor || !editor.isEditable) return false

  const activeImage = getActiveImageNodeName(editor)
  if (activeImage) {
    return editor.isActive(activeImage, { textAlign: align })
  }

  return editor.isActive({ textAlign: align })
}

/**
 * Aplica alinhamento:
 * 1) Se imagem ativa → updateAttributes(IMAGE_NODE, { textAlign })
 * 2) Se imagem adjacente → seleciona e updateAttributes
 * 3) Senão → setTextAlign (parágrafo/heading/etc.)
 */
export function setTextAlign(editor: Editor | null, align: TextAlign): boolean {
  if (!editor || !editor.isEditable) return false

  const activeImage = getActiveImageNodeName(editor)
  if (activeImage) {
    return editor.chain().focus().updateAttributes(activeImage, { textAlign: align }).run()
  }

  const adjacentImage = selectAdjacentImageAndGetName(editor)
  if (adjacentImage) {
    return editor.chain().focus().updateAttributes(adjacentImage, { textAlign: align }).run()
  }

  if (!isExtensionAvailable(editor, "textAlign")) return false
  const chain = editor.chain().focus()
  if (hasSetTextAlign(chain)) {
    return chain.setTextAlign(align).run()
  }
  return false
}

/**
 * Mostrar botão? Mantém visível quando há suporte a texto ou imagem.
 */
export function shouldShowButton(props: {
  editor: Editor | null
  hideWhenUnavailable: boolean
  align: TextAlign
}): boolean {
  const { editor, hideWhenUnavailable, align } = props
  if (!editor || !editor.isEditable) return false

  const hasTextAlign = isExtensionAvailable(editor, "textAlign")
  const hasAnyImage = IMAGE_NODES.some((n) => isExtensionAvailable(editor, n))
  if (!hasTextAlign && !hasAnyImage) return false

  if (hideWhenUnavailable && !editor.isActive("code")) {
    return canSetTextAlign(editor, align)
  }

  return true
}

/**
 * Hook principal
 */
export function useTextAlign(config: UseTextAlignConfig) {
  const {
    editor: providedEditor,
    align,
    hideWhenUnavailable = false,
    onAligned,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = React.useState<boolean>(true)
  const canAlign = canSetTextAlign(editor, align)
  const isActive = isTextAlignActive(editor, align)

  React.useEffect(() => {
    if (!editor) return

    const handleUpdate = () => {
      setIsVisible(shouldShowButton({ editor, align, hideWhenUnavailable }))
    }

    handleUpdate()
    editor.on("selectionUpdate", handleUpdate)
    editor.on("transaction", handleUpdate)

    return () => {
      editor.off("selectionUpdate", handleUpdate)
      editor.off("transaction", handleUpdate)
    }
  }, [editor, hideWhenUnavailable, align])

  const handleTextAlign = React.useCallback(() => {
    if (!editor) return false
    const ok = setTextAlign(editor, align)
    if (ok) onAligned?.()
    return ok
  }, [editor, align, onAligned])

  return {
    isVisible,
    isActive,
    handleTextAlign,
    canAlign,
    label: textAlignLabels[align],
    shortcutKeys: TEXT_ALIGN_SHORTCUT_KEYS[align],
    Icon: textAlignIcons[align],
  }
}
